import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import Pagination from '../components/main/Pagination';
import useRidesApi from '../api/useRidesApi';
import RidesStatesCard from '../components/rides/RidesStatesCard';
import RideDonutChart from '../components/rides/RideDonutChart';
import RideBarChart from '../components/rides/RideBarChart';
import { debounce } from '../helper/debounce';
import dayjs from 'dayjs';
import CommonLoader from '../components/loaders/CommonLoader';
import { Avatar, Dialog } from '@mui/material';
import { MdClose } from 'react-icons/md';
import { toast } from 'react-toastify';
import { MdPhone } from 'react-icons/md';
import PhoneNumberModal from '../components/modals/PhoneNumberModal';
import CallConfirmationDialog from '../components/modals/CallConfirmationDialog';
import useAdminsApi from '../api/useAdminsApi';
import { useAuth } from '../contexts/AuthContext';
import useUsersApi from '../api/useUsersApi';
import WhatsAppConfirmationDialog from '../components/modals/WhatsAppConfirmationDialog';
import usePlacesApi from '../api/usePlacesApi';

const Rides = () => {
    const { getStates, loading, getRides, rejectRide, viewRide, getNearbyDrivers } = useRidesApi();
    const { getProfile } = useAdminsApi();
    const [activeTab, setActiveTab] = useState('partner');
    const [filter, setFilter] = useState("daily");
    const [start_date, setStartDate] = useState("");
    const [end_date, setEndDate] = useState("");
    const [statusfilter, setStatusfilter] = useState("all");
    const [states, setStates] = useState(null);
    const [data, setData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchFilter, setSearchFilter] = useState('booking_number');
    const [page, setPage] = useState(1);
    const [total_pages, setTotalPages] = useState(1);
    const [reason, setReason] = useState("");
    const [openRejectForm, setOpenRejectForm] = useState(false);
    const [selectedRide, setSelectedRide] = useState(null);
    const [openView, setOpenView] = useState(false);
    const [booking, setRide] = useState(null);
    const [selectedPhone, setSelectedPhone] = useState(null);
    const [openPhone, setOpenPhone] = useState(false);
    const [callDialogOpen, setCallDialogOpen] = useState(false);
    const [callLoading, setCallLoading] = useState(false);
    const { profile, permissions, isActionAllowed } = useAuth();
    const { makeCall, sendWhatsAppNotification } = useUsersApi();
    const { getPlacesForBooking } = usePlacesApi();
    const [whatsappLoading, setWhatsappLoading] = useState(false);
    const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false);
    const [places, setPlaces] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedPlaceId, setSelectedPlaceId] = useState(null);
    const [nearbyDrivers, setNearbyDrivers] = useState([]);
    const [loadingDrivers, setLoadingDrivers] = useState(false);
    console.log("activeTab", activeTab);
    const formatDate = (date) => date.toISOString().split("T")[0];

    useEffect(() => {
        const daily = new Date();
        let start, end;

        switch (filter) {
            case "daily":
                start = end = daily;
                break;
            case "weekly":
                start = new Date(daily);
                start.setDate(daily.getDate() - 6);
                end = daily;
                break;
            case "monthly":
                start = new Date(daily.getFullYear(), daily.getMonth(), 1);
                end = new Date(daily.getFullYear(), daily.getMonth() + 1, 0);
                break;
            case "yearly":
                start = new Date(daily.getFullYear(), 0, 1);
                end = new Date(daily.getFullYear(), 11, 31);
                break;
            case "custom":
                return; // Don't auto-set dates for custom filter
            default:
                return;
        }

        setStartDate(formatDate(start));
        setEndDate(formatDate(end));
    }, [filter]);

    const handleFilterChange = (e) => {
        const value = e.target.value;
        setFilter(value);
    };

    const handleDateChange = (e, dateType) => {
        const value = e.target.value;
        if (dateType === 'start') {
            setStartDate(value);
            // If end date is before start date, update end date
            if (end_date && value > end_date) {
                setEndDate(value);
            }
        } else {
            // Don't allow end date to be before start date
            if (!start_date || value >= start_date) {
                setEndDate(value);
            }
        }
    };

    const handleClear = () => {
        setFilter("daily");
        setStartDate("");
        setEndDate("");
    };

    const fetchStates = async () => {
        try {
            const params = { filter };
            if (filter === 'custom' && start_date && end_date) {
                params.start_date = start_date;
                params.end_date = end_date;
            }
            const res = await getStates(params);
            setStates(res?.data?.payload?.calculated_info);
        } catch (error) {
            console.error("Error fetching states:", error);
        }
    }

    useEffect(() => {
        fetchStates();
    }, [filter, start_date, end_date]);

    const showPhone = (phone) => {
        setSelectedPhone(phone)
        setOpenPhone(true)
    }

    const handleCallClick = (phone) => {
        setSelectedPhone(phone);
        setCallDialogOpen(true);
    };

    const handleCallConfirm = async () => {
        if (!selectedPhone) return;
        try {
            setCallLoading(true);
            await makeCall(selectedPhone);
            toast.success('Call initiated successfully!');
        } catch (err) {
            // error handled by useApi hook
        } finally {
            setCallLoading(false);
            setCallDialogOpen(false);
        }
    };

    const handleWhatsAppClick = (phone) => {
        setSelectedPhone(phone);
        setWhatsappDialogOpen(true);
    };

    const handleWhatsAppConfirm = async () => {
        if (!selectedPhone) return;
        try {
            setWhatsappLoading(true);
            await sendWhatsAppNotification(selectedPhone);
            toast.success('WhatsApp notification sent successfully!');
        } catch (err) {
            // error handled by useApi hook
        } finally {
            setWhatsappLoading(false);
            setWhatsappDialogOpen(false);
        }
    };

    const handleOpenRejectForm = (booking) => {
        setSelectedRide(booking);
        setOpenRejectForm(true)
    }

    const fetchNearbyDrivers = async (bookingObj) => {
        let pickupId = bookingObj?.pickup_destination_id;
        let dropId = bookingObj?.drop_destination_id;

        if (!pickupId && bookingObj?.destination_docs?.length >= 2) {
            pickupId = bookingObj.destination_docs[0]._id;
            dropId = bookingObj.destination_docs[bookingObj.destination_docs.length - 1]._id;
        } else if (!pickupId && bookingObj?.all_destination?.length >= 2) {
            pickupId = bookingObj.all_destination[0]._id;
            dropId = bookingObj.all_destination[bookingObj.all_destination.length - 1]._id;
        }

        if (!pickupId || !dropId) return;

        setLoadingDrivers(true);
        try {
            const res = await getNearbyDrivers({
                pickup_destination_id: pickupId,
                drop_destination_id: dropId
            });
            setNearbyDrivers(res?.data?.payload?.drivers || res?.data?.drivers || res?.data?.payload?.driverlist || res?.data?.payload || []);
        } catch (error) {
            console.error("Error fetching nearby drivers:", error);
        } finally {
            setLoadingDrivers(false);
        }
    };



    const handleView = async (booking) => {
        setSelectedRide(booking);
        setOpenView(true)
        const res = await viewRide(booking?._id)
        const bookingObj = res?.data?.payload?.booking_object;
        setRide(bookingObj);
        if (bookingObj) {
            fetchNearbyDrivers(bookingObj);
        }
    }

    const handleStatusFilterChange = (e) => {
        setStatusfilter(e.target.value);
    };

    const handlePlaceSelect = (place) => {
        setSearchTerm(place.city_name);
        setSelectedPlaceId(place._id);
        setShowSuggestions(false);
        fetchRides(1, place.city_name, statusfilter, place._id);
        setPage(1);
    };

    const fetchRides = async (page = 1, search = '', currentStatusFilter = 'all', placeId = selectedPlaceId) => {
        let filters = {
            is_wts_booking: activeTab === 'whatsapp',
            is_kyc_booking: activeTab === 'kyc',
            is_available_vehicals: activeTab === 'free_vehicle',
            is_exchange_booking: activeTab === 'exchange_duty'
        };

        if (activeTab === 'partner') {
            filters.is_wts_booking = false;
            filters.is_kyc_booking = false;
        }

        // Add search filter to the filters object
        if (search && searchFilter) {
            filters.search_filter = searchFilter;
            if (placeId && (searchFilter === 'pickup_location' || searchFilter === 'drop_location')) {
                filters.place_id = placeId;
            } else {
                filters.search = search;
            }
        }

        const res = await getRides(page, "", filters, currentStatusFilter);
        setData(res.data.payload.all_bookings);
        setTotalPages(res.data.payload.total_pages);
    };

    const debouncedfetchRides = useCallback(
        debounce((value) => {
            fetchRides(1, value, statusfilter);
            setPage(1);
        }, 500),
        [statusfilter, searchFilter, activeTab]
    );

    const handleSearchChange = async (e) => {
        let value = e.target.value;

        if (searchFilter === "booking_number") {
            // Only allow numbers
            value = value.replace(/\D/g, '');
        }

        setSearchTerm(value);
        setSelectedPlaceId(null); // Reset placeId when typing new search

        if ((searchFilter === "pickup_location" || searchFilter === "drop_location") && value.length > 2) {
            try {
                const res = await getPlacesForBooking(value);
                setPlaces(res?.data?.payload?.placesData || []);
                setShowSuggestions(true);
            } catch (err) {
                console.error("Error fetching places:", err);
            }
        } else {
            setShowSuggestions(false);
            debouncedfetchRides(value);
        }
    };

    const handleSearchFilterChange = (e) => {
        setSearchFilter(e.target.value);
        setSearchTerm("");
        setSelectedPlaceId(null);
        setShowSuggestions(false);
        fetchRides(1, "", statusfilter);
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    useEffect(() => {
        fetchRides(page, searchTerm, statusfilter);
    }, [page, activeTab, statusfilter, searchFilter]);

    const handleRejectRide = async (e) => {
        e.preventDefault();
        const payload = {
            "booking_id": selectedRide._id,
            "closed_reason": reason
        }
        await rejectRide(payload)
        setOpenView(false)
        setReason("")
        setOpenRejectForm(false)
        toast.success("Ride Closed")
        fetchRides(page, searchTerm);
    }


    const StatusBadge = (status) => {
        const statusStyles = {
            '0': {
                text: "Pending",
                className:
                    "bg-warning-focus text-warning-600 border border-warning-main px-24 py-4 radius-4 fw-medium text-sm",
            },
            '1': {
                text: "Accepted",
                className:
                    "bg-info-focus text-info-600 border border-info-main px-24 py-4 radius-4 fw-medium text-sm",
            },
            '2': {
                text: "Confirmed",
                className:
                    "bg-primary-focus text-primary-600 border border-primary-main px-24 py-4 radius-4 fw-medium text-sm",
            },
            '3': {
                text: "Completed",
                className:
                    "bg-success-focus text-success-600 border border-success-main px-24 py-4 radius-4 fw-medium text-sm",
            },
            '4': {
                text: "Cancelled",
                className:
                    "bg-danger text-white border border-danger px-24 py-4 radius-4 fw-medium text-sm",
            },
            '5': {
                text: "Closed",
                className:
                    "bg-secondary text-white border border-secondary-main px-24 py-4 radius-4 fw-medium text-sm",
            },
        };

        const statusInfo = statusStyles[status] || {
            text: "Unknown",
            className:
                "bg-gray-200 text-gray-600 border border-gray-400 px-24 py-4 radius-4 fw-medium text-sm",
        };

        return <span className={statusInfo.className}>{statusInfo.text}</span>;
    };


    const rideCategoryLabel = {
        0: 'Outstation',
        1: 'Rental',
        2: 'Sharing',
    };

    const rideWayLabel = {
        1: 'One Way',
        2: 'Round Trip',
    };

    return (
        <div>
            <div className='card-header'>
                <div className='d-flex align-items-center flex-wrap gap-2 justify-content-between mb-24'>
                    <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 '>
                        <h6 className='fw-semibold mb-0'>Rides</h6>
                    </div>
                    <div className="d-flex gap-2 flex-wrap align-items-center">
                        <select
                            className='form-select form-select-sm w-auto bg-base border text-secondary-light'
                            value={filter}
                            onChange={handleFilterChange}
                        >
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                            <option value="yearly">Yearly</option>
                            <option value="custom">Custom</option>
                        </select>

                        {filter === "custom" && (
                            <>
                                <input
                                    type="date"
                                    className="form-control form-control-sm w-auto"
                                    value={start_date}
                                    onChange={(e) => handleDateChange(e, 'start')}
                                    max={end_date || undefined}
                                />
                                <span>to</span>
                                <input
                                    type="date"
                                    className="form-control form-control-sm w-auto"
                                    value={end_date}
                                    onChange={(e) => handleDateChange(e, 'end')}
                                    min={start_date || undefined}
                                />
                            </>
                        )}

                        {filter !== "weekly" && (
                            <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={handleClear}
                            >
                                Clear
                            </button>
                        )}
                    </div>
                </div>
            </div>
            {(states === null && loading) ?
                <div>
                    <CommonLoader />
                </div>
                :
                <>
                    <div className="">
                        <RidesStatesCard states={states} />
                    </div>
                    <div className="row my-24">
                        {/* Left Column */}
                        <div className="row mb-4">
                            <div className='col-xxl-8 col-md-12'>
                                <div className='card h-100'>
                                    <RideBarChart first_chart_data={states?.first_chart_data} />
                                </div>
                            </div>

                            <div className='col-xxl-4 col-md-12'>
                                <div className='card h-100'>
                                    <div className='card-body'>
                                        <div className='d-flex align-items-center flex-wrap gap-2 justify-content-between'>
                                            <h6 className='mb-2 fw-bold text-lg mb-0'>Rides</h6>
                                        </div>
                                        <RideDonutChart second_chart_data={states?.second_chart_data} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            }
            {states !== null &&
                <div className="card radius-12">
                    <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                        <ul className="nav nav-tabs">
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'partner' ? 'active' : ''}`}
                                    onClick={() => {
                                        setActiveTab('partner');
                                        setPage(1);
                                    }}
                                >
                                    Partner Rides
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'whatsapp' ? 'active' : ''}`}
                                    onClick={() => {
                                        setActiveTab('whatsapp');
                                        setPage(1);
                                    }}
                                >
                                    WhatsApp Rides
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'kyc' ? 'active' : ''}`}
                                    onClick={() => {
                                        setActiveTab('kyc');
                                        setPage(1);
                                    }}
                                >
                                    KYC Rides
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'free_vehicle' ? 'active' : ''}`}
                                    onClick={() => {
                                        setActiveTab('free_vehicle');
                                        setPage(1);
                                    }}
                                >
                                    Free Vehicle
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'exchange_duty' ? 'active' : ''}`}
                                    onClick={() => {
                                        setActiveTab('exchange_duty');
                                        setPage(1);
                                    }}
                                >
                                    Exchange Duty
                                </button>
                            </li>
                        </ul>

                        <div className="d-flex gap-2 flex-wrap align-items-center">
                            <div className='d-flex justify-content-center align-items-center gap-2'>
                                <form className="navbar-search position-relative">
                                    <input
                                        type="text"
                                        className="bg-base h-40-px"
                                        name="search"
                                        placeholder="Search rides..."
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                    />
                                    <Icon icon="ion:search-outline" className="icon" />
                                    {showSuggestions && places.length > 0 && (
                                        <div
                                            className="position-absolute border radius-8 shadow-lg w-100 mt-1 scroll-sm bg-base border-neutral-200"
                                            style={{
                                                zIndex: 9999,
                                                maxHeight: '300px',
                                                top: '100%',
                                                left: 0,
                                                overflowY: 'auto'
                                            }}
                                        >
                                            {places.map((place) => (
                                                <div
                                                    key={place._id}
                                                    className="p-12 cursor-pointer bg-hover-neutral-50 text-sm border-bottom border-neutral-100 last-border-none d-flex flex-column align-items-start transition-all"
                                                    onClick={() => handlePlaceSelect(place)}
                                                >
                                                    <span className="fw-semibold text-secondary-light mb-2">{place.city_name}</span>
                                                    <span className="text-xs text-secondary-light opacity-75">{place.state_name}, {place.country_name}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </form>
                                <select
                                    className='form-select form-select-sm w-auto bg-base border text-secondary-light'
                                    value={searchFilter}
                                    onChange={handleSearchFilterChange}
                                >
                                    <option value="booking_number">Booking Number</option>
                                    <option value="pickup_location">Pickup Location</option>
                                    <option value="drop_location">Drop Location</option>
                                </select>
                            </div>

                            <select
                                className='form-select form-select-sm w-auto bg-base border text-secondary-light'
                                value={statusfilter}
                                onChange={handleStatusFilterChange}
                            >
                                <option value="all">All</option>
                                <option value="0">Pending</option>
                                <option value="1">Accepted</option>
                                <option value="2">Confirmed</option>
                                <option value="3">Completed</option>
                                <option value="4">Cancelled</option>
                                <option value="5">Closed</option>
                            </select>
                        </div>
                    </div>

                    <div className="card-body p-24">
                        {(!openView || !openRejectForm) && loading ? (
                            <CommonLoader />
                        ) : (
                            <>
                                <div className="table-responsive scroll-sm">
                                    <table className="table bordered-table sm-table mb-0">
                                        <thead>
                                            <tr>
                                                {activeTab === 'free_vehicle' ? (
                                                    <>
                                                        <th className="text-start">Ride Number</th>
                                                        <th className="text-start">Owner</th>
                                                        <th className="text-start">Status</th>
                                                        <th className="text-center">Destination</th>
                                                        <th className="text-center">Due Date</th>
                                                        <th className="text-center">Owner Phone</th>
                                                        <th className="text-center">Created At</th>
                                                        <th className="text-center">Action</th>
                                                    </>
                                                ) : activeTab === 'exchange_duty' ? (
                                                    <>
                                                        <th className="text-start">Ride Number</th>
                                                        <th className="text-start">Owner</th>
                                                        <th className="text-center">Pickup</th>
                                                        <th className="text-center">Drop</th>
                                                        <th className="text-center">Due Date</th>
                                                        <th className="text-center">Status</th>
                                                        <th className="text-center">Owner Phone</th>
                                                        <th className="text-center">Created At</th>
                                                        <th className="text-center">Action</th>
                                                    </>
                                                ) : (
                                                    <>
                                                        <th className="text-start">Ride Number</th>
                                                        {activeTab === 'partner' && (
                                                            <>
                                                                <th className="text-center">Admin</th>
                                                                <th className="text-center">Added By</th>
                                                            </>
                                                        )}
                                                        {activeTab === 'whatsapp' && profile?.user_type === 'superadmin' && (
                                                            <th className="text-center">Name</th>
                                                        )}
                                                        <th className="text-center">Pickup</th>
                                                        <th className="text-center">Drop</th>
                                                        <th className="text-center">Status</th>
                                                        <th className="text-center">Created At</th>
                                                        <th className="text-center">Action</th>
                                                    </>
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.length > 0 ? (
                                                data.map((booking) => {
                                                    return (
                                                        <tr key={booking?._id || booking?.id}>
                                                            {activeTab === 'free_vehicle' ? (
                                                                <>
                                                                    <td className='text-start'>{booking?.available_vehical_number || '-'}</td>
                                                                    <td className='text-start'>
                                                                        <div className="d-flex align-items-center gap-2">
                                                                            <Avatar
                                                                                src={booking?.cab_owner_id?.profile_photo}
                                                                                alt={booking?.cab_owner_id?.user_name}
                                                                                sx={{ width: 32, height: 32 }}
                                                                            />
                                                                            <span className="fw-medium">{booking?.cab_owner_id?.user_name || '-'}</span>
                                                                        </div>
                                                                    </td>
                                                                    <td className="text-center">
                                                                        <span
                                                                            className={`badge ${booking?.cab_status ? "bg-danger" : "bg-success"
                                                                                }`}
                                                                        >
                                                                            {booking?.cab_status ? "Unbooked" : "Booked"}
                                                                        </span>
                                                                    </td>
                                                                    <td className='text-center'>
                                                                        {booking?.available_destination
                                                                            ? `${booking.available_destination.city_name}, ${booking.available_destination.state_name}`
                                                                            : '-'}
                                                                    </td>
                                                                    <td className='text-center'>{booking?.due_date || '-'}</td>
                                                                    <td className='text-center'>{booking?.phone_number || '-'}</td>
                                                                </>
                                                            ) : activeTab === 'exchange_duty' ? (
                                                                <>
                                                                    <td className='text-start'>{booking?.booking_number || '-'}</td>
                                                                    <td className='text-start'>
                                                                        <div className="d-flex align-items-center gap-2">
                                                                            <Avatar
                                                                                src={booking?.cab_owner_id?.profile_photo}
                                                                                alt={booking?.cab_owner_id?.user_name}
                                                                                sx={{ width: 32, height: 32 }}
                                                                            />
                                                                            <span className="fw-medium">{booking?.cab_owner_id?.user_name || '-'}</span>
                                                                        </div>
                                                                    </td>
                                                                    <td className='text-center'>
                                                                        {booking?.pickup_destination?.city_name
                                                                            ? (booking.pickup_destination.city_name + ", " + booking.pickup_destination.state_name)
                                                                            : '-'}
                                                                    </td>
                                                                    <td className='text-center'>
                                                                        {booking?.drop_destination?.city_name
                                                                            ? (booking.drop_destination.city_name + ", " + booking.drop_destination.state_name)
                                                                            : '-'}
                                                                    </td>
                                                                    <td className='text-center'>{booking?.due_date || '-'}</td>
                                                                    <td className='text-center'>{StatusBadge(booking?.booking_status)}</td>
                                                                    <td className='text-center'>{booking?.phone_number || '-'}</td>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <td className='text-start'>{booking?.booking_number}</td>
                                                                    {activeTab === 'partner' && (
                                                                        <>
                                                                            <td>
                                                                                <div className="d-flex align-items-center gap-2">
                                                                                    <span className="fw-medium">{booking?.agent_id?.parent_user_id?.user_name || '-'}</span>
                                                                                </div>
                                                                            </td>
                                                                            <td className="text-center">
                                                                                <span className="fw-medium">{booking?.agent_id?.phone_number || '-'}</span>
                                                                            </td>
                                                                        </>
                                                                    )}
                                                                    {activeTab === 'whatsapp' && profile?.user_type === 'superadmin' && (
                                                                        <td className="text-center">{booking?.data_type || '-'}</td>
                                                                    )}
                                                                    <td className='text-center'>
                                                                        {booking?.pickup_destination?.city_name
                                                                            ? (booking.pickup_destination.city_name + ", " + booking.pickup_destination.state_name)
                                                                            : booking?.all_destination?.[0]?.city_name
                                                                                ? (booking.all_destination[0].city_name + ", " + booking.all_destination[0].state_name)
                                                                                : '-'}
                                                                    </td>
                                                                    <td className='text-center'>
                                                                        {booking?.drop_destination?.city_name
                                                                            ? (booking.drop_destination.city_name + ", " + booking.drop_destination.state_name)
                                                                            : booking?.all_destination?.[1]?.city_name
                                                                                ? (booking.all_destination[1].city_name + ", " + booking.all_destination[1].state_name)
                                                                                : '-'}
                                                                    </td>
                                                                    <td className='text-center'>{StatusBadge(booking?.booking_status)}</td>
                                                                </>
                                                            )}
                                                            <td className="text-center">
                                                                {dayjs(booking?.createdAt).format('DD MMM YYYY hh:mm A')}
                                                            </td>
                                                            <td className="text-center">
                                                                <div className="d-flex justify-content-center gap-2">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleView(booking)}
                                                                        className="bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                                    >
                                                                        <Icon
                                                                            icon="majesticons:eye-line"
                                                                            className="icon text-xl"
                                                                        />
                                                                    </button>
                                                                    {isActionAllowed('Rides', 'edit') && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleOpenRejectForm(booking)}
                                                                            className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                                        >
                                                                            <Icon icon="akar-icons:cross" />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan="10" className="text-center py-4">No Data Found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
                                    <span>Page {page} of {total_pages}</span>
                                    <Pagination
                                        total_pages={total_pages}
                                        current_page={page}
                                        onPageChange={handlePageChange}
                                    />
                                </div>
                            </>)}
                    </div>
                </div>

            }

            <Dialog
                open={openRejectForm}
                onClose={() => setOpenRejectForm(false)}
                maxWidth={false} // Disable MUI's default maxWidth
                fullWidth={false}
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        width: '500px', // Custom width here
                    }
                }}
            >
                <form className="card mb-0" onSubmit={(e) => handleRejectRide(e)}>
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h6 className="card-title mb-0">Cancel Ride (#{selectedRide?.booking_number})</h6>
                        <button
                            type="button"
                            className="btn btn-sm btn-light p-1 d-flex justify-content-center align-items-center"
                            onClick={() => setOpenRejectForm(false)}
                        >
                            <MdClose size={18} />
                        </button>
                    </div>

                    <div className="card-body">

                        <div className="mb-3">
                            <label className="form-label fw-bold text-sm mb-1">Reason</label>
                            <textarea
                                required
                                className="form-control"
                                rows="4"
                                placeholder="Write reason"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                            />
                        </div>

                        <div className="d-flex justify-content-end mt-3">
                            <button className="btn btn-danger px-8 py-4 radius-8" disabled={loading} type="submit">
                                {loading ? (
                                    <span className="spinner-border spinner-border-sm" />
                                ) : (
                                    "Cancel Ride"
                                )}
                            </button>
                        </div>
                    </div>
                </form>
            </Dialog>

            <Dialog
                open={openView}
                onClose={() => setOpenView(false)}
                fullWidth
                maxWidth={'lg'}
                PaperProps={{
                    sx: { borderRadius: '16px', backgroundColor: 'transparent', boxShadow: 'none' },
                }}
            >
                <div className="card shadow-none mb-0 border-0">
                    <div className="card-header d-flex justify-content-between align-items-center border-bottom bg-base">
                        <h6 className="card-title mb-0">Ride Details</h6>
                        <button
                            type="button"
                            className="btn btn-sm btn-light-focus p-1 d-flex justify-content-center align-items-center radius-8"
                            onClick={() => setOpenView(false)}
                        >
                            <MdClose size={18} />
                        </button>
                    </div>
                    {!openRejectForm && loading ? (
                        <CommonLoader />) :
                        <div className="card-body p-24 bg-base">
                            <div className="row g-4">
                                {/* Top Section: Status and Basic Info */}
                                <div className="col-12">
                                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 p-20 bg-neutral-50 radius-12 border border-neutral-100">
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="w-48-px h-48-px bg-primary-50 rounded-circle d-flex justify-content-center align-items-center">
                                                <Icon icon="solar:hashtag-square-outline" className="text-primary-600 text-2xl" />
                                            </div>
                                            <div>
                                                <h6 className="mb-0 fw-bold">#{booking?.booking_number}</h6>
                                                <span className="text-secondary-light text-sm">Ride Booking Number</span>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-4">
                                            <div className="text-end">
                                                <div className="text-secondary-light text-xs mb-1 fw-bold uppercase">BOOKING STATUS</div>
                                                {StatusBadge(booking?.booking_status)}
                                            </div>
                                            <div className="border-start border-neutral-200 ps-4">
                                                <div className="text-secondary-light text-xs mb-1 fw-bold uppercase">RIDE DATE</div>
                                                <div className="fw-semibold text-md">
                                                    {booking?.is_wts_booking ? dayjs(booking?.createdAt).format('DD MMM, YYYY') : dayjs(booking?.dueDate).format('DD MMM, YYYY')}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Main Content: Agent, Cab, and Locations */}
                                <div className="col-lg-7">
                                    <div className="card h-100 border border-neutral-100 shadow-none radius-12">
                                        <div className="card-header border-bottom py-12 px-20">
                                            <h6 className="text-md fw-bold mb-0 d-flex align-items-center gap-2">
                                                <Icon icon="solar:map-point-wave-outline" className="text-primary-600" />
                                                Route Information
                                            </h6>
                                        </div>
                                        <div className="card-body p-20">
                                            <div className="table-responsive">
                                                <table className="table bordered-table sm-table mb-0">
                                                    <thead>
                                                        <tr>
                                                            <th className="text-secondary-light fw-bold text-xs py-2 px-3 border-bottom bg-neutral-50">CITY</th>
                                                            <th className="text-secondary-light fw-bold text-xs py-2 px-3 text-end border-bottom bg-neutral-50">STATE</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {(booking?.destination_docs || booking?.all_destination)?.map((item, idx, arr) => (
                                                            <tr key={idx} className="border-bottom-0">
                                                                <td className="py-3 px-3">
                                                                    <div className="d-flex align-items-center gap-3">
                                                                        <div className={`w-10-px h-10-px rounded-circle ${idx === 0 ? 'bg-success-main' : idx === (arr.length - 1) ? 'bg-danger-main' : 'bg-warning-main'}`}></div>
                                                                        <span className="fw-medium text-secondary-light">{item?.city_name || "N/A"}</span>
                                                                    </div>
                                                                </td>
                                                                <td className="py-3 px-3 text-end text-secondary-light opacity-75">{item?.state_name || "N/A"}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                            {booking?.note && (
                                                <div className="mt-20 p-16 bg-warning-50 radius-8 border border-warning-200">
                                                    <div className="text-xs fw-bold text-warning-main mb-8 d-flex align-items-center gap-2">
                                                        <Icon icon="solar:info-circle-outline" className="text-lg" /> RIDE NOTE
                                                    </div>
                                                    <p className="mb-0 text-sm text-secondary-light lh-base">{booking?.note}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="col-lg-5">
                                    <div className="row g-3 h-100">
                                        {/* Agent Information */}
                                        <div className="col-12">
                                            <div className="card border border-neutral-100 shadow-none radius-12 bg-base">
                                                <div className="card-header border-bottom py-12 px-20 d-flex justify-content-between align-items-center">
                                                    <h6 className="text-md fw-bold mb-0 d-flex align-items-center gap-2">
                                                        <Icon icon="solar:user-speak-outline" className="text-primary-600" />
                                                        Agent Details
                                                    </h6>
                                                    {booking?.agent_id?._id && (
                                                        <Link
                                                            to={`/users-list/${booking.agent_id._id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="btn btn-sm btn-primary-600 px-12 py-6 radius-8 d-flex align-items-center gap-2"
                                                        >
                                                            <Icon icon="majesticons:eye-line" className="text-lg" />
                                                            View Details
                                                        </Link>
                                                    )}
                                                </div>
                                                <div className="card-body p-20">
                                                    <div className="d-flex align-items-center gap-3 mb-20">
                                                        <Avatar src={booking?.agent_id?.profile_photo} sx={{ width: 56, height: 56, border: '2px solid #6b728033' }} />
                                                        <div>
                                                            <h6 className="mb-4 fw-bold text-secondary-light">{booking?.agent_id?.user_name}</h6>
                                                            <div className="text-neutral-500 text-sm">{booking?.agent_id?.email}</div>
                                                        </div>
                                                    </div>
                                                    <div className="p-16 bg-neutral-50 radius-8 d-flex align-items-center justify-content-between border border-neutral-100">
                                                        <div>
                                                            <div className="text-xs text-neutral-500 mb-4 fw-bold uppercase">Contact Number</div>
                                                            <div className="fw-bold text-secondary-light">{booking?.from_number}</div>
                                                        </div>
                                                        <div className="d-flex gap-2">
                                                            <button
                                                                onClick={() => handleWhatsAppClick(booking?.from_number)}
                                                                className="bg-info-100 text-info-600 hover-bg-info-200 transition-all w-44-px h-44-px d-flex justify-content-center align-items-center rounded-circle border-0"
                                                            >
                                                                <Icon icon="ic:baseline-whatsapp" className="text-xl" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleCallClick(booking?.from_number)}
                                                                className="bg-warning-100 text-warning-main hover-bg-warning-200 transition-all w-44-px h-44-px d-flex justify-content-center align-items-center rounded-circle border-0"
                                                                title="Call"
                                                            >
                                                                <Icon icon="akar-icons:phone" className="text-xl" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="p-16 bg-neutral-50 radius-8 d-flex align-items-center justify-content-between border border-neutral-100 mt-1">
                                                        <div>
                                                            <div className="text-xs text-neutral-500 mb-4 fw-bold uppercase">Customer Number</div>
                                                            <div className="fw-bold text-secondary-light">{booking?.phone_number}</div>
                                                        </div>
                                                        <div className="d-flex gap-2">
                                                            <button
                                                                onClick={() => handleWhatsAppClick(booking?.phone_number)}
                                                                className="bg-info-100 text-info-600 hover-bg-info-200 transition-all w-44-px h-44-px d-flex justify-content-center align-items-center rounded-circle border-0"
                                                            >
                                                                <Icon icon="ic:baseline-whatsapp" className="text-xl" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleCallClick(booking?.phone_number)}
                                                                className="bg-warning-100 text-warning-main hover-bg-warning-200 transition-all w-44-px h-44-px d-flex justify-content-center align-items-center rounded-circle border-0"
                                                                title="Call"
                                                            >
                                                                <Icon icon="akar-icons:phone" className="text-xl" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Cab Information */}
                                        <div className="col-12">
                                            <div className="card border border-neutral-100 shadow-none radius-12 bg-base">
                                                <div className="card-header border-bottom py-12 px-20">
                                                    <h6 className="text-md fw-bold mb-0 d-flex align-items-center gap-2">
                                                        <Icon icon="solar:bus-outline" className="text-primary-600" />
                                                        Vehicle Info
                                                    </h6>
                                                </div>
                                                <div className="card-body p-20">
                                                    <div className="d-flex align-items-center gap-4">
                                                        <div className="bg-neutral-50 p-12 radius-12 border border-neutral-100">
                                                            <img src={booking?.cab_type?.vehicle_img} alt="Cab" style={{ height: "60px", objectFit: 'contain' }} />
                                                        </div>
                                                        <div>
                                                            <div className="text-xs text-neutral-500 mb-4 fw-bold uppercase">SELECTED CAB TYPE</div>
                                                            <h6 className="mb-0 fw-bold text-primary-600 text-lg">{booking?.cab_type?.name}</h6>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Nearby Drivers Section */}
                                <div className="col-12 mt-24">
                                    <div className="card border border-neutral-100 shadow-none radius-12 bg-base">
                                        <div className="card-header border-bottom py-12 px-20">
                                            <h6 className="text-md fw-bold mb-0 d-flex align-items-center gap-2">
                                                <Icon icon="solar:users-group-two-rounded-outline" className="text-primary-600" />
                                                Nearby Drivers
                                            </h6>
                                        </div>
                                        <div className="card-body p-20">
                                            {loadingDrivers ? (
                                                <CommonLoader />
                                            ) : nearbyDrivers && nearbyDrivers.length > 0 ? (
                                                <div className="table-responsive">
                                                    <table className="table bordered-table sm-table mb-0">
                                                        <thead>
                                                            <tr>
                                                                <th>Name</th>
                                                                <th>Mobile</th>
                                                                <th>Duty Status</th>
                                                                <th>Document Status</th>
                                                                <th>Subscription Days</th>
                                                                <th>Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {nearbyDrivers.map((driver, idx) => (
                                                                <tr key={driver._id || idx}>
                                                                    <td>{driver.user_name || driver.name || "-"}</td>
                                                                    <td>{driver.phone_number || "-"}</td>
                                                                    <td>
                                                                        <span className={`badge ${driver.duty_status ? "bg-success" : "bg-danger"}`}>
                                                                            {driver.duty_status ? "On Duty" : "Off Duty"}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        {driver.aadhaar_document_status === 0 ? (
                                                                            <span className="badge bg-white text-dark">Pending</span>
                                                                        ) : driver.aadhaar_document_status === 1 ? (
                                                                            <span className="badge bg-warning text-dark">In Review</span>
                                                                        ) : driver.aadhaar_document_status === 2 ? (
                                                                            <span className="badge bg-success">Verified</span>
                                                                        ) : driver.aadhaar_document_status === 3 ? (
                                                                            <span className="badge bg-danger">Rejected</span>
                                                                        ) : <span className="badge bg-secondary">N/A</span>}
                                                                    </td>
                                                                    <td>{driver.subscription_days || 0}</td>
                                                                    <td>
                                                                        <div className="d-flex gap-2">
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleWhatsAppClick(driver?.phone_number)}
                                                                                className="bg-info-100 text-info-600 hover-bg-info-200 transition-all w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle border-0"
                                                                                title="WhatsApp"
                                                                            >
                                                                                <Icon icon="ic:baseline-whatsapp" className="text-sm" />
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleCallClick(driver?.phone_number)}
                                                                                className="bg-warning-100 text-warning-main hover-bg-warning-200 transition-all w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle border-0"
                                                                                title="Call"
                                                                            >
                                                                                <Icon icon="akar-icons:phone" className="text-sm" />
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : (
                                                <div className="text-center text-secondary-light py-3">No nearby drivers found.</div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Footer: Actions */}
                                <div className="col-12 mt-24">
                                    <div className="d-flex justify-content-end gap-3 pt-24 border-top border-neutral-100">
                                        {booking?.booking_status !== 3 && activeTab === "partner" && isActionAllowed('Rides', 'edit') ? (
                                            <button
                                                type="button"
                                                onClick={() => handleOpenRejectForm(booking)}
                                                className="btn btn-danger-600 px-24 py-12 radius-8 d-flex align-items-center gap-2 fw-bold"
                                            >
                                                <Icon icon="solar:close-circle-outline" className="text-xl" />
                                                Cancel Ride
                                            </button>
                                        ) : isActionAllowed('Rides', 'edit') ? (
                                            profile.userType === "superadmin" &&
                                            [0, 1, 2].includes(booking?.booking_status) &&
                                            ["whatsapp", "kyc"].includes(booking?.rideType) && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleOpenRejectForm(booking)}
                                                    className="btn btn-danger-600 px-24 py-12 radius-8 d-flex align-items-center gap-2 fw-bold"
                                                >
                                                    <Icon icon="solar:close-circle-outline" className="text-xl" />
                                                    Cancel Ride
                                                </button>
                                            )
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                </div>
            </Dialog>

            {selectedPhone && <PhoneNumberModal open={openPhone} setOpen={setOpenPhone} user_name={""} phoneNumber={selectedPhone} />}
            {selectedPhone && (
                <CallConfirmationDialog
                    open={callDialogOpen}
                    onClose={() => setCallDialogOpen(false)}
                    onConfirm={handleCallConfirm}
                    userName={""}
                    phoneNumber={selectedPhone}
                    loading={callLoading}
                />
            )}
            {selectedPhone && (
                <WhatsAppConfirmationDialog
                    open={whatsappDialogOpen}
                    onClose={() => setWhatsappDialogOpen(false)}
                    onConfirm={handleWhatsAppConfirm}
                    userName={""}
                    phoneNumber={selectedPhone}
                    loading={whatsappLoading}
                />
            )}

        </div>
    );
};

export default Rides;
